// env.schema.ts imports this module directly in Node without Nuxt aliases.
import { isRecord } from '../lib/isRecord.ts'

const NUMERIC_PATTERN = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i

function iterableValue(value: unknown): unknown[] {
  if (Array.isArray(value)) return value

  if (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Iterable<unknown>)[Symbol.iterator] === 'function'
  ) {
    return Array.from(value as Iterable<unknown>)
  }

  return []
}

/**
 * Coerce a boundary value to a non-empty string, or the fallback.
 *
 * An empty (or all-whitespace, via `trimmedStringValue`) string yields the FALLBACK, not
 * `''` — empty and absent are deliberately indistinguishable here. Two consequences worth
 * knowing before building on this: a "required" check layered on top rejects a
 * legitimately-empty field, and an `optionalString`-style wrapper turns "clear this value"
 * into "omit this key" on the way back out. Reach for a plain `typeof value === 'string'`
 * check when empty must survive.
 */
function stringValue(value: unknown, fallback: string | null = null): string | null {
  if (value === null || value === undefined) return fallback

  const type = typeof value
  const isStringableObject =
    type === 'object' &&
    typeof (value as { toString?: unknown }).toString === 'function' &&
    value.toString !== Object.prototype.toString

  if (
    type === 'string' ||
    type === 'number' ||
    type === 'boolean' ||
    type === 'bigint' ||
    isStringableObject
  ) {
    const string = String(value)
    return string !== '' ? string : fallback
  }

  return fallback
}

function trimmedStringValue(value: unknown, fallback: string | null = null): string | null {
  const string = stringValue(value)
  if (string === null) return fallback

  const trimmed = string.trim()
  return trimmed !== '' ? trimmed : fallback
}

/** Mirrors Laravel's `TrimStrings` + `ConvertEmptyStringsToNull`; non-strings pass through untouched. */
function blankToNull(value: unknown): unknown {
  if (value === undefined || value === null) return value
  if (typeof value !== 'string') return value

  const trimmed = value.trim()
  return trimmed !== '' ? trimmed : null
}

function firstStringValue(fallback: string | null, ...values: unknown[]): string | null {
  for (const value of values) {
    const string = stringValue(value)
    if (string !== null) return string
  }

  return fallback
}

function intValue(value: unknown, fallback: number | null = 0): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : fallback
  }

  if (typeof value === 'boolean') return value ? 1 : 0

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return NUMERIC_PATTERN.test(trimmed) ? Math.trunc(Number.parseFloat(trimmed)) : fallback
  }

  const string = stringValue(value)
  if (string !== null) {
    const trimmed = string.trim()
    return NUMERIC_PATTERN.test(trimmed) ? Math.trunc(Number.parseFloat(trimmed)) : fallback
  }

  return fallback
}

function floatValue(value: unknown, fallback: number | null = 0.0): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback
  }

  if (typeof value === 'boolean') return value ? 1 : 0

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return NUMERIC_PATTERN.test(trimmed) ? Number.parseFloat(trimmed) : fallback
  }

  const string = stringValue(value)
  if (string !== null) {
    const trimmed = string.trim()
    return NUMERIC_PATTERN.test(trimmed) ? Number.parseFloat(trimmed) : fallback
  }

  return fallback
}

const TRUE_WORDS = ['1', 'true', 'yes', 'on']
const FALSE_WORDS = ['0', 'false', 'no', 'off']

function boolWord(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim().toLowerCase()
    : ''
}

function isBoolValue(value: unknown): boolean {
  const word = boolWord(value)

  return typeof value === 'boolean' || TRUE_WORDS.includes(word) || FALSE_WORDS.includes(word)
}

function boolValue(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value

  const word = boolWord(value)

  if (TRUE_WORDS.includes(word)) return true
  if (FALSE_WORDS.includes(word)) return false

  return fallback
}

/**
 * Narrow an unknown boundary value to an array, WITHOUT checking its elements.
 *
 * `T` is an unchecked assertion: this proves the value is an array and nothing more.
 * Do not treat a call as validation of the contents — filter or map the elements
 * through a per-element guard (`stringList`, `intList`, `listOfAssocArrays`, or a
 * local predicate) before handing them to anything that trusts their type.
 */
function arrayValue<T>(value: unknown, fallback: T[] = []): T[] {
  return Array.isArray(value) ? (value as T[]) : fallback
}

function assocArray(
  value: unknown,
  fallback: Record<string, unknown> = {}
): Record<string, unknown> {
  return isRecord(value) ? { ...value } : fallback
}

function listOfAssocArrays(
  value: unknown,
  fallback: Record<string, unknown>[] = []
): Record<string, unknown>[] {
  if (!Array.isArray(value) && !(value !== null && typeof value === 'object')) return fallback

  const items: Record<string, unknown>[] = []
  for (const item of iterableValue(value)) {
    if (isRecord(item)) items.push(item)
  }

  return items
}

function stringList(value: unknown, fallback: string[] = []): string[] {
  if (!Array.isArray(value) && !(value !== null && typeof value === 'object')) return fallback

  const items: string[] = []
  for (const item of iterableValue(value)) {
    const string = stringValue(item)
    if (string !== null) items.push(string)
  }

  return items
}

function trimmedStringList(value: unknown, fallback: string[] = []): string[] {
  let items: string[]

  if (typeof value === 'string') {
    items = stringList(value.split(','))
  } else if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
    items = stringList(value)
  } else if (value === null || value === undefined) {
    return fallback
  } else {
    items = stringList([value])
  }

  const trimmedItems: string[] = []
  for (const item of items) {
    const trimmed = item.trim()
    if (trimmed !== '') trimmedItems.push(trimmed)
  }

  return trimmedItems
}

function intList(value: unknown, fallback: number[] = []): number[] {
  let source: unknown

  if (typeof value === 'string') {
    source = value.split(',')
  } else if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
    source = value
  } else if (value === null || value === undefined) {
    return fallback
  } else {
    source = [value]
  }

  const items: number[] = []
  for (const item of iterableValue(source)) {
    const id = intValue(item, 0) ?? 0
    if (id > 0) items.push(id)
  }

  return items
}

function distinctIntList(value: unknown, fallback: number[] = []): number[] {
  return Array.from(new Set(intList(value, fallback)))
}

const WEBSITE_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//

/** Mirrors `Normalization::websiteUrlValue`; prepends `https://` to a bare domain or `//`-relative value. */
function websiteUrl(value: unknown, fallback: string | null = null): string | null {
  if (Array.isArray(value)) return fallback

  const string = trimmedStringValue(value)
  if (string === null) return fallback

  if (string.startsWith('//')) return `https:${string}`
  if (WEBSITE_SCHEME_PATTERN.test(string)) return string

  return `https://${string}`
}

const UNBOUNDED_INTEGER_PATTERN = /^[+-]?\d+$/

/** Client mirror of the write convention: an empty input defaults to `Infinity`. */
function unbounded(value: unknown): unknown {
  if (value === undefined || value === null) return Infinity
  if (typeof value === 'number') return value
  if (typeof value !== 'string') return value

  const trimmed = value.trim()
  if (trimmed === '') return Infinity
  if (trimmed.toLowerCase() === 'infinity') return Infinity

  return UNBOUNDED_INTEGER_PATTERN.test(trimmed) ? Number(trimmed) : value
}

function enumValue<T extends string>(value: unknown, validValues: readonly T[]): T | null
function enumValue<T extends string, F>(
  value: unknown,
  validValues: readonly T[],
  fallback: F
): T | F
function enumValue(
  value: unknown,
  validValues: readonly string[],
  fallback: unknown = null
): unknown {
  const string = stringValue(value)
  if (string === null) return fallback

  const lowered = string.toLowerCase()
  return validValues.find((validValue) => validValue.toLowerCase() === lowered) ?? fallback
}

// --- deepEqual and JSON Schema predicates (ported from @studnicky/predicates) ---
//
// These ASSERT whether a value satisfies a constraint: (value, constraint) -> boolean.
// That is a different contract from the rest of this file's (value, fallback) -> coerced
// value shape, so they don't take a fallback — but they belong in the same substrate
// because they are the other half of "handling guards/normalization of data models in
// one uniform place": coercion and validation of the same boundary data.

function compareMaps(left: Map<unknown, unknown>, right: Map<unknown, unknown>): boolean {
  if (left.size !== right.size) return false

  for (const [key, leftVal] of left.entries()) {
    if (!right.has(key)) return false
    if (!deepEqual(leftVal, right.get(key))) return false
  }

  return true
}

/**
 * Set members are matched structurally, not by identity.
 *
 * `right.has(item)` alone would make `deepEqual(new Set([{a:1}]), new Set([{a:1}]))`
 * false while the same two objects compare equal everywhere else in this function —
 * an asymmetry that silently reports "changed" for unchanged data. Identity is tried
 * first because it is the common case and O(1); the structural scan is the fallback.
 */
function compareSets(left: Set<unknown>, right: Set<unknown>): boolean {
  if (left.size !== right.size) return false

  const unmatched = new Set(right)

  for (const item of left.values()) {
    if (unmatched.delete(item)) continue

    let matched = false
    for (const candidate of unmatched) {
      if (deepEqual(item, candidate)) {
        unmatched.delete(candidate)
        matched = true
        break
      }
    }

    if (!matched) return false
  }

  return true
}

function compareObjects(left: Record<string, unknown>, right: Record<string, unknown>): boolean {
  return compareMaps(new Map(Object.entries(left)), new Map(Object.entries(right)))
}

/**
 * Structural deep equality for any JavaScript value.
 *
 * Handles: primitives, NaN, Date, RegExp, Set, Map, Array, plain objects.
 * Does NOT deeply merge class instances beyond the types listed above.
 */
function deepEqual(left: unknown, right: unknown): boolean {
  if (typeof left === 'number' && typeof right === 'number') {
    if (Number.isNaN(left) && Number.isNaN(right)) return true
  }

  if (left === right) return true
  if (left === null || right === null) return false
  if (typeof left !== typeof right) return false
  if (typeof left !== 'object' || typeof right !== 'object') return false

  if (left instanceof Date && right instanceof Date) return left.getTime() === right.getTime()
  if (left instanceof Date || right instanceof Date) return false

  if (left instanceof RegExp && right instanceof RegExp) return left.toString() === right.toString()
  if (left instanceof RegExp || right instanceof RegExp) return false

  if (left instanceof Set && right instanceof Set) return compareSets(left, right)
  if (left instanceof Set || right instanceof Set) return false

  if (left instanceof Map && right instanceof Map) return compareMaps(left, right)
  if (left instanceof Map || right instanceof Map) return false

  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) return false
    const leftLen = left.length
    for (let i = 0; i < leftLen; i++) {
      if (!deepEqual(left[i], right[i])) return false
    }
    return true
  }
  if (Array.isArray(left) || Array.isArray(right)) return false

  if (isRecord(left) && isRecord(right)) return compareObjects(left, right)

  return false
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isIntegerValue(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value)
}

function checkPattern(value: string, pattern: RegExp): boolean {
  pattern.lastIndex = 0
  const result = pattern.test(value)
  pattern.lastIndex = 0

  return result
}

/** Count Unicode code points without allocating an intermediate array. */
function codePointLength(str: string): number {
  let length = 0
  const strLen = str.length

  for (let index = 0; index < strLen; index++) {
    length++
    const code = str.codePointAt(index)

    if (code !== undefined && code > 0xff_ff) index++
  }

  return length
}

/** Returns false as soon as code-point count exceeds `limit`; stops early. */
function codePointLengthAtMost(str: string, limit: number): boolean {
  let count = 0
  const strLen = str.length

  for (let index = 0; index < strLen; index++) {
    count++
    if (count > limit) return false
    const code = str.codePointAt(index)

    if (code !== undefined && code > 0xff_ff) index++
  }

  return true
}

/** Fast-path: code_points <= utf16_length, so value.length<=max is definitely true. */
function satisfiesMaxLength(value: string, maximum: number): boolean {
  if (value.length <= maximum) return true

  return codePointLengthAtMost(value, maximum)
}

function satisfiesMinProperties(value: object, minimum: number): boolean {
  return Object.keys(value).length >= minimum
}

/**
 * Drop `undefined`-valued keys from an object built from boundary-coercion
 * helpers (e.g. `stringValue`, `floatValue`) before it's assigned to a DTO
 * whose optional fields are declared without `| undefined`. Under
 * `exactOptionalPropertyTypes`, `{ foo: string | undefined }` is not
 * assignable to `{ foo?: string }` even though omitting `foo` is — this
 * closes that gap by both stripping the key at runtime and narrowing the
 * static type to match (required keys stay required, only keys whose
 * inferred type includes `undefined` become optional-without-undefined).
 */
type DefinedOnly<T> = { [K in keyof T as undefined extends T[K] ? never : K]: T[K] } & {
  [K in keyof T as undefined extends T[K] ? K : never]?: Exclude<T[K], undefined>
}

function omitUndefined<T extends Record<string, unknown>>(obj: T): DefinedOnly<T> {
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (value !== undefined) result[key] = value
  }
  return result as DefinedOnly<T>
}

export const Normalization = {
  arrayValue,
  assocArray,
  blankToNull,
  boolValue,
  isBoolValue,
  checkPattern,
  codePointLength,
  deepEqual,
  distinctIntList,
  enumValue,
  firstStringValue,
  floatValue,
  intList,
  intValue,
  isFiniteNumber,
  isIntegerValue,
  isRecord,
  listOfAssocArrays,
  omitUndefined,
  satisfiesMaxLength,
  satisfiesMinProperties,
  stringList,
  stringValue,
  trimmedStringList,
  trimmedStringValue,
  unbounded,
  websiteUrl
}
